import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [imprintOpen, setImprintOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  return (
    <>
      <footer className="bg-[#1A1A1A] text-white mx-3 md:mx-6 rounded-3xl overflow-visible py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 w-full">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-x-4 gap-y-6 md:gap-10 mb-6 md:mb-8">

            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4">
              <div className="flex items-center gap-2 mb-2">
                <img
                  src="https://static.readdy.ai/image/bd37f9b321d8d487bb2df5b36d703d09/5bc315f20d8b71200f0abd862b82a739.png"
                  alt="Ski Service Kurier Logo"
                  className="h-10 md:h-14 w-auto object-contain"
                />
                <img
                  src="https://public.readdy.ai/ai/img_res/2d695602-b797-4700-9252-8f49008eff8f.png"
                  alt="Klosters"
                  className="h-7 md:h-9 w-auto object-contain brightness-0 invert"
                />
              </div>
              <p className="text-xs md:text-base lg:text-lg font-bold">
                {t('footer.tagline')}
              </p>
            </div>

            {/* Service Column */}
            <div className="col-span-1 md:col-span-2">
              <h4 className="text-xs font-bold uppercase tracking-wider mb-2 md:mb-3">{t('footer.serviceTitle')}</h4>
              <ul className="space-y-1.5">
                <li><a href="#service" className="text-gray-400 hover:text-[#2B8FBF] transition-colors text-xs cursor-pointer">{t('footer.serviceKleiner')}</a></li>
                <li><a href="#service" className="text-gray-400 hover:text-[#2B8FBF] transition-colors text-xs cursor-pointer">{t('footer.serviceGrosser')}</a></li>
                <li><a href="#service" className="text-gray-400 hover:text-[#2B8FBF] transition-colors text-xs cursor-pointer">{t('footer.serviceRenn')}</a></li>
              </ul>
            </div>

            {/* Legal Column */}
            <div className="col-span-1 md:col-span-2">
              <h4 className="text-xs font-bold uppercase tracking-wider mb-2 md:mb-3">{t('footer.legalTitle')}</h4>
              <ul className="space-y-1.5">
                <li>
                  <button
                    onClick={() => setTermsOpen(true)}
                    className="text-gray-400 hover:text-[#2B8FBF] transition-colors text-xs cursor-pointer bg-transparent border-none p-0 text-left whitespace-nowrap"
                  >
                    {t('footer.legalTerms')}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setPrivacyOpen(true)}
                    className="text-gray-400 hover:text-[#2B8FBF] transition-colors text-xs cursor-pointer bg-transparent border-none p-0 text-left whitespace-nowrap"
                  >
                    {t('footer.legalPrivacy')}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setImprintOpen(true)}
                    className="text-gray-400 hover:text-[#2B8FBF] transition-colors text-xs cursor-pointer bg-transparent border-none p-0 text-left whitespace-nowrap"
                  >
                    {t('footer.legalImprint')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div className="col-span-1 md:col-span-2">
              <h4 className="text-xs font-bold uppercase tracking-wider mb-2 md:mb-3">{t('footer.contactTitle')}</h4>
              <ul className="space-y-1.5">
                <li>
                  <a href="mailto:klosters@ski-service-kurier.ch" className="flex items-start gap-1.5 text-gray-400 hover:text-[#2B8FBF] transition-colors text-xs cursor-pointer">
                    <i className="ri-mail-line flex-shrink-0 mt-0.5"></i>
                    <span className="break-all leading-tight">klosters@ski-service-kurier.ch</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Newsletter Column */}
            <div className="col-span-2 md:col-span-4">
              <h4 className="text-xs font-bold uppercase tracking-wider mb-2 md:mb-3">{t('footer.newsletterTitle')}</h4>
              <p className="text-gray-400 text-xs mb-2 md:mb-3">
                {t('footer.newsletterDescription')}
              </p>
              <form className="relative w-full" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  placeholder={t('footer.newsletterPlaceholder')}
                  className="w-full bg-gray-800 text-white px-4 py-2.5 pr-12 rounded-full focus:outline-none focus:ring-2 focus:ring-[#2B8FBF] text-xs md:text-sm"
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1 w-8 h-8 md:w-10 md:h-10 bg-[#2B8FBF] rounded-full flex items-center justify-center hover:bg-[#2480ad] transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  <i className="ri-arrow-right-line text-white text-sm"></i>
                </button>
              </form>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-2">
              <p className="text-gray-400 text-xs text-center md:text-left">
                {t('footer.copyright', { year: currentYear })}
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2B8FBF] text-xs transition-colors cursor-pointer"
              >
                {t('footer.poweredBy')}
              </a>
            </div>
          </div>

          {/* Watermark */}
          <div className="mt-3 md:mt-4 text-center overflow-visible">
            <p className="text-[#2A2A2A] text-lg sm:text-3xl md:text-7xl font-black opacity-50 break-words">
              {t('footer.watermark')}
            </p>
          </div>
        </div>
      </footer>

      {/* Impressum Modal */}
      {imprintOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setImprintOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setImprintOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
              aria-label="Schliessen"
            >
              <i className="ri-close-line text-gray-600 text-lg"></i>
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-6 uppercase tracking-wide">
              {t('footer.legalImprint')}
            </h2>
            <div className="space-y-4 text-sm text-gray-700">
              <div>
                <p className="font-semibold text-gray-900">{t('imprint.companyName')}</p>
                <p>{t('imprint.street')}</p>
                <p>{t('imprint.city')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">{t('imprint.contactLabel')}</p>
                <a
                  href="mailto:klosters@ski-service-kurier.ch"
                  className="text-[#2B8FBF] hover:underline cursor-pointer"
                >
                  klosters@ski-service-kurier.ch
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AGB Modal */}
      {termsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setTermsOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setTermsOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
              aria-label="Schliessen"
            >
              <i className="ri-close-line text-gray-600 text-lg"></i>
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1 uppercase tracking-wide">
              {t('terms.title')}
            </h2>
            <p className="text-xs text-gray-400 mb-5">{t('terms.lastUpdated')}</p>
            <div className="overflow-y-auto flex-1 space-y-4 text-sm text-gray-700 pr-1">
              {(['s1','s2','s3','s4','s5','s6','s7'] as const).map((s) => (
                <div key={s}>
                  <p className="font-semibold text-gray-900 mb-1">{t(`terms.${s}Title`)}</p>
                  <p className="leading-relaxed">{t(`terms.${s}Text`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Datenschutz Modal */}
      {privacyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setPrivacyOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPrivacyOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
              aria-label="Schliessen"
            >
              <i className="ri-close-line text-gray-600 text-lg"></i>
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-1 uppercase tracking-wide">
              {t('privacy.title')}
            </h2>
            <p className="text-xs text-gray-400 mb-5">{t('privacy.lastUpdated')}</p>
            <div className="overflow-y-auto flex-1 space-y-4 text-sm text-gray-700 pr-1">
              {(['s1','s2','s3','s4','s5','s6','s7','s8','s9'] as const).map((s) => (
                <div key={s}>
                  <p className="font-semibold text-gray-900 mb-1">{t(`privacy.${s}Title`)}</p>
                  <p className="leading-relaxed">{t(`privacy.${s}Text`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
